import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const submitContact = async (payload) => {
  const { data } = await axios.post(`${API}/contact`, payload);
  return data;
};

export const emailDiagnosticResult = async (payload) => {
  const { data } = await axios.post(`${API}/diagnostic/email-result`, payload);
  return data;
};

export const analyzeDiagnostic = async (payload) => {
  // Large reports can make the LLM analysis long-running, so the backend processes it as a
  // background job and we poll for the result. Every request stays short (no proxy timeouts).
  const { data } = await axios.post(`${API}/diagnostic/analyze`, payload);
  if (data && data.result) return data.result;          // fast-path if ever returned inline
  const jobId = data && data.job_id;
  if (!jobId) return data;
  const deadline = Date.now() + 240000;                 // wait up to ~4 minutes for big files
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  while (Date.now() < deadline) {
    await wait(2500);
    try {
      const { data: s } = await axios.get(`${API}/diagnostic/analyze/${jobId}`);
      if (s.status === "done") return s.result;
      if (s.status === "failed") throw new Error("analysis_failed");
    } catch (e) {
      if (e && e.message === "analysis_failed") throw e;
      // transient network/poll hiccup — keep polling until the deadline
    }
  }
  throw new Error("analysis_timeout");
};

export const ingestDiagnosticMaterials = async (formData) => {
  const { data } = await axios.post(`${API}/diagnostic/materials`, formData);
  return data;
};

export const submitReportAnalysis = async (formData) => {
  const { data } = await axios.post(`${API}/report-analysis`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

// ---------- Industry news feed ----------
export const fetchIndustryArticles = async (limit = 12) => {
  const { data } = await axios.get(`${API}/industry-articles`, { params: { limit } });
  return data;
};

export const fetchAdminIndustryArticles = async () => {
  const { data } = await axios.get(`${API}/admin/industry-articles`, { withCredentials: true });
  return data;
};

export const toggleIndustryArticle = async (uid, hidden) => {
  const { data } = await axios.patch(`${API}/admin/industry-articles/${uid}`, { hidden }, { withCredentials: true });
  return data;
};

export const deleteIndustryArticle = async (uid) => {
  const { data } = await axios.delete(`${API}/admin/industry-articles/${uid}`, { withCredentials: true });
  return data;
};

export const refreshIndustryArticles = async () => {
  const { data } = await axios.post(`${API}/admin/industry-articles/refresh`, {}, { withCredentials: true });
  return data;
};

export const fetchChatAnalytics = async () => {
  const { data } = await axios.get(`${API}/admin/chat/analytics`, { withCredentials: true });
  return data;
};

// ---------- Admin ----------
// The admin session lives in an httpOnly cookie set by the backend on login,
// so the token is never exposed to JavaScript (XSS-safe). Every admin request
// sends the cookie via `withCredentials`.
export const adminLogin = async (password) => {
  const { data } = await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
  return data;
};

export const checkAdminSession = async () => {
  try {
    await axios.get(`${API}/admin/me`, { withCredentials: true });
    return true;
  } catch {
    return false;
  }
};

export const adminLogout = async () => {
  try {
    await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
  } catch {
    // Best-effort: the client session resets regardless of a network error here.
  }
};

export const fetchInquiries = async () => {
  const { data } = await axios.get(`${API}/contact`, { withCredentials: true });
  return data;
};

export const fetchBlocklist = async () => {
  const { data } = await axios.get(`${API}/admin/blocklist`, { withCredentials: true });
  return data;
};

export const addBlocklistEntry = async (type, value, note) => {
  const { data } = await axios.post(`${API}/admin/blocklist`, { type, value, note }, { withCredentials: true });
  return data;
};

export const removeBlocklistEntry = async (id) => {
  const { data } = await axios.delete(`${API}/admin/blocklist/${id}`, { withCredentials: true });
  return data;
};

export const fetchReportAnalyses = async () => {
  const { data } = await axios.get(`${API}/report-analysis`, { withCredentials: true });
  return data;
};

export const updateLeadAttribution = async (kind, id, attribution) => {
  const { data } = await axios.patch(
    `${API}/admin/leads/${kind}/${id}/attribution`,
    { attribution },
    { withCredentials: true }
  );
  return data;
};

export const downloadReportFile = async (filePath, fileName) => {
  const res = await fetch(`${API}/files/${filePath}`, { credentials: "include" });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "report.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
